import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  SECONDARY_BRANDS,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Emotional module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 */

export const emotionalModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    supportMarkets: true,
    module: "IMAGERY",
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
    supportBase: true,
    defaultBase: "GENPOP",
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportScoreType: false,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["BUBBLE", "BAR", "PROFILE"],
    defaultVisualisationType: "BUBBLE",
    supportStatTesting: true,
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Segment TREND has TRENDS_METRIC but no TRENDS_RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "corporate", label: "Corporate" },
              { value: "customerCentric", label: "Customer-centric" },
              { value: "dependable", label: "Dependable" },
              { value: "fun", label: "Fun" },
              { value: "goodValue", label: "Good Value" },
              { value: "hip", label: "Hip" },
              { value: "innovative", label: "Innovative" },
              { value: "intelligent", label: "Intelligent" },
              { value: "practical", label: "Practical" },
              { value: "premium", label: "Premium" },
              { value: "simple", label: "Simple" },
              { value: "sociallyConscious", label: "Socially Conscious" },
              { value: "smart", label: "Smart" },
              { value: "stylish", label: "Stylish" },
              { value: "traditional", label: "Traditional" },
              { value: "trustworthy", label: "Trustworthy" },
              { value: "unconventional", label: "Unconventional" },
              { value: "visionary", label: "Visionary" },
              { value: "wholesome", label: "Wholesome" },
              { value: "classy", label: "Classy" },
              { value: "sophisticated", label: "Sophisticated" },
              { value: "young", label: "Young" },
              { value: "energetic", label: "Energetic" },
              { value: "confident", label: "Confident" },
              { value: "bold", label: "Bold" },
            ],
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Brand TREND has TRENDS_METRIC but no TRENDS_RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "corporate", label: "Corporate" },
              { value: "customerCentric", label: "Customer-centric" },
              { value: "dependable", label: "Dependable" },
              { value: "fun", label: "Fun" },
              { value: "goodValue", label: "Good Value" },
              { value: "hip", label: "Hip" },
              { value: "innovative", label: "Innovative" },
              { value: "intelligent", label: "Intelligent" },
              { value: "practical", label: "Practical" },
              { value: "premium", label: "Premium" },
              { value: "simple", label: "Simple" },
              { value: "sociallyConscious", label: "Socially Conscious" },
              { value: "smart", label: "Smart" },
              { value: "stylish", label: "Stylish" },
              { value: "traditional", label: "Traditional" },
              { value: "trustworthy", label: "Trustworthy" },
              { value: "unconventional", label: "Unconventional" },
              { value: "visionary", label: "Visionary" },
              { value: "wholesome", label: "Wholesome" },
              { value: "classy", label: "Classy" },
              { value: "sophisticated", label: "Sophisticated" },
              { value: "young", label: "Young" },
              { value: "energetic", label: "Energetic" },
              { value: "confident", label: "Confident" },
              { value: "bold", label: "Bold" },
            ],
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportVisualisationSwitch: true,
            supportedVisualisationTypes: [
              "BUBBLE",
              "BAR",
              "PROFILE",
              "DIFFERENTIATION",
              "PERCEPTUAL",
              "BUTTERFLY",
            ],
            // defaultVisualisationType intentionally omitted here —
            // inherits "BUBBLE" from the module-level config.
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
            DIFFERENTIATION: {
              config: {
                supportSecondaryBrands: true,
                maximumAllowedBrands: 1,
                minimumRequiredSecondaryBrands: 1,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SECONDARY_BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },

            PERCEPTUAL: {
              config: {
                minimumRequiredBrands: 3,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },

            BUTTERFLY: {
              config: {
                supportSecondaryBrands: true,
                maximumAllowedBrands: 1,
                maximumAllowedSecondaryBrands: 1,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SECONDARY_BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  BASE,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // Emotional By Market TREND has TRENDS_METRIC but no TRENDS_RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              { value: "corporate", label: "Corporate" },
              { value: "customerCentric", label: "Customer-centric" },
              { value: "dependable", label: "Dependable" },
              { value: "fun", label: "Fun" },
              { value: "goodValue", label: "Good Value" },
              { value: "hip", label: "Hip" },
              { value: "innovative", label: "Innovative" },
              { value: "intelligent", label: "Intelligent" },
              { value: "practical", label: "Practical" },
              { value: "premium", label: "Premium" },
              { value: "simple", label: "Simple" },
              { value: "sociallyConscious", label: "Socially Conscious" },
              { value: "smart", label: "Smart" },
              { value: "stylish", label: "Stylish" },
              { value: "traditional", label: "Traditional" },
              { value: "trustworthy", label: "Trustworthy" },
              { value: "unconventional", label: "Unconventional" },
              { value: "visionary", label: "Visionary" },
              { value: "wholesome", label: "Wholesome" },
              { value: "classy", label: "Classy" },
              { value: "sophisticated", label: "Sophisticated" },
              { value: "young", label: "Young" },
              { value: "energetic", label: "Energetic" },
              { value: "confident", label: "Confident" },
              { value: "bold", label: "Bold" },
            ],
            defaultTrendsMetric: "corporate",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },
  },
};
